import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import AuthHeader from '../../components/AuthHeader'
import api from '../../utils/api'
import toast from 'react-hot-toast'

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  // Check if email field is filled
  const isFormValid = email.trim() !== ''

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const response = await api.post('/password-reset/forgot-password', {
        email
      })

      if (response.data.success) {
        setEmailSent(true)
        toast.success('Password reset email sent!')
      } else {
        toast.error(response.data.message || 'Failed to send reset email')
      }
    } catch (error) {
      console.error('Forgot password error:', error)
      toast.error(error.response?.data?.message || 'Failed to send reset email')
    } finally {
      setLoading(false)
    }
  }

  const handleResendEmail = async () => {
    setResendLoading(true)

    try {
      const response = await api.post('/password-reset/forgot-password', {
        email
      })

      if (response.data.success) {
        toast.success('Password reset email sent again!')
      } else {
        toast.error(response.data.message || 'Failed to resend reset email')
      }
    } catch (error) {
      console.error('Resend email error:', error)
      toast.error(error.response?.data?.message || 'Failed to resend reset email')
    } finally {
      setResendLoading(false)
    }
  }

  if (emailSent) {
    return (
      <div className="min-h-screen bg-black">
        <div className='p-4'>
          <div className="border-2 border-white">
            <AuthHeader />
            <div className='bg-[#141414] p-4 w-full min-h-screen flex items-center justify-center'>
              <div className="w-full max-w-sm relative">
                {/* Success Message Card */}
                <div className="bg-card border border-gray-700 p-6 relative">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h1 className="text-2xl font-bold text-white mb-2 font-avenir">Check Your Email</h1>
              <p className="text-gray-300 mb-6">
                We've sent a password reset link to <strong>{email}</strong>
              </p>
              
              <div className="space-y-4">
                <p className="text-sm text-gray-400">
                  Didn't receive the email? Check your spam folder or resend.
                </p>
                
                <button
                  onClick={handleResendEmail}
                  disabled={resendLoading}
                  className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-700 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded transition-colors font-avenir"
                >
                  {resendLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Sending...</span>
                    </div>
                  ) : (
                    'Resend Email'
                  )}
                </button>
                
                <Link
                  to="/login"
                  className="block w-full text-center text-gray-400 hover:text-white transition-colors font-avenir"
                >
                  Back to Login
                </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black">
      <div className='p-4'>
        <div className="border-2 border-white">
          <AuthHeader />
          <div className='bg-[#141414] p-4 w-full min-h-screen flex items-center justify-center'>
            <div className="w-full max-w-sm relative">
        {/* Form Card */}
        <div className="bg-card border border-gray-700 p-6 relative">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-white mb-2 font-avenir">Forgot Password?</h1>
            <p className="text-gray-400 text-sm">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 flex flex-col gap-4">
            {/* Email Field */}
            <div className="space-y-1">
     
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-10 px-3 py-2 bg-white border-0 placeholder:text-center text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all font-avenir"
                placeholder="Enter your email address"
                required
                disabled={loading}
              />
            </div>

            {/* Submit Button */}
            <div className='!mx-6'>
              <button
                type="submit"
                disabled={loading || !isFormValid}
                className="w-full h-10 bg-red-600 hover:bg-red-700 disabled:bg-red-700 disabled:cursor-not-allowed text-white font-bold text-sm rounded-lg transition-colors flex items-center justify-center uppercase tracking-wide font-avenir"
              >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Sending...</span>
                </div>
              ) : (
                'Send Reset Link'
              )}
              </button>
            </div>
          </form>

          {/* Footer Links */}
          <div className="mt-6 text-center space-y-2">
            <Link
              to="/login"
              className="block text-sm text-gray-400  hover:text-red-300 transition-colors font-avenir"
            >
              Remember your password? Sign in
            </Link>
            <Link
              to="/signup"
              className="block text-sm text-gray-400 hover:text-red-300 transition-colors font-avenir"
            >
              Don't have an account? Sign up
            </Link>
          </div>

          {/* Privacy & Terms Links */}
          <div className="mt-6 pt-4 border-t border-gray-700 flex flex-wrap justify-center gap-3 text-xs">
            <Link
              to="/privacy-policy"
              className="text-gray-400 hover:text-red-300 transition-colors font-avenir"
            >
              Privacy Policy
            </Link>
            <span className="text-gray-600">|</span>
            <Link
              to="/terms-of-use"
              className="text-gray-400 hover:text-red-300 transition-colors font-avenir"
            >
              Terms of Use
            </Link>
          </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  )
}

export default ForgotPasswordPage
